import { User, UserDetailedInterface, UserInterface } from 'app/core/user/models';

import { ErgoTeamMemberInterface } from '../models/ergo-team-member.model';

import { UserActions, UserActionType } from './actions';
import { UserState, UserStateInterface } from './models';

const initialState: UserState = new UserState({
  ids: [],
  users: {},
  detailedUsers: {},
});

export function userReducer(
  state: UserStateInterface = initialState,
  action: UserActions): UserStateInterface {
  switch (action.type) {

    // Update the state when we get a new list
    case UserActionType.LoadUsersCompleted:
      const allUsers: UserInterface[] = action.payload as UserInterface[];
      const newUsers: UserInterface[] = allUsers.filter((user: UserInterface) => !state.users[user.id]);
      const newUserIds: number[] = newUsers.map((user: UserInterface) => user.id);
      const newUserEntities: { [id: number]: UserInterface } = newUsers.reduce(
        (users: { [id: number]: UserInterface }, user: UserInterface) => {
          return { ...users, ...{
            [user.id]: new User(user),
          }};
        },
        {});

      return {
        ...state,
        ids: state.ids.concat(newUserIds),
        users: { ...state.users, ...newUserEntities },
      };

    // Clear the detailed users list when a new load is requested
    case UserActionType.LoadDetailedUsers:
      return {
        ...state,
        ids: [],
        detailedUsers: {},
      };

    // Update the state when we get a new list of detailed users
    case UserActionType.LoadDetailedUsersCompleted:
      const allDetailedUsers: UserDetailedInterface[] = action.payload as UserDetailedInterface[];
      const newDetailedUserIds: number[] = allDetailedUsers.map((detailedUser: UserDetailedInterface) => detailedUser.user.id);
      const newDetailedUserEntities: { [id: number]: UserDetailedInterface } = allDetailedUsers.reduce(
        (detailedUsers: { [id: number]: UserDetailedInterface }, detailedUser: UserDetailedInterface) => {
          return { ...detailedUsers, ...{
            [detailedUser.user.id]: detailedUser,
          }};
        },
        {});
      return {
        ...state,
        ids: newDetailedUserIds ? newDetailedUserIds : [],
        detailedUsers: newDetailedUserEntities ? newDetailedUserEntities : {},
      };

    case UserActionType.LoadAdminUsersCompleted:
      const payloadUsers: UserInterface[] = action.payload as UserInterface[];
      const allAdminUsers: UserInterface[] = payloadUsers.map((user: UserInterface) => new User(user));
      return {
        ...state,
        adminUsers: allAdminUsers,
      };

    case UserActionType.UserDetailRequest:
    case UserActionType.UserDetailRequestRejected:
      return {
        ...state,
        ids: undefined,
        users: undefined,
        selectedUser: undefined,
        detailedUsers: undefined,
      };

    case UserActionType.UserUpdated:
      const updatedUser: UserInterface = action.payload as UserInterface;
      return {
        ...state,
        users: { ...state.users, [updatedUser.id]:updatedUser },
      };

    case UserActionType.ResetSelectedUser:
      return {
        ...state,
        selectedUser: undefined,
      };

    case UserActionType.SelectUser:
      return {
        ...state,
        selectedUser: undefined,
      };

    case UserActionType.SelectUserCompleted:
      return {
        ...state,
        selectedUser: action.payload as number,
      };

    case UserActionType.LoadSingleUser:
    case UserActionType.CreateUser:
      return {
        ...state,
        selectedProfileUser: undefined,
      };

    case UserActionType.LoadSingleUserCompleted:
      const payloadUser: UserInterface = action.payload;
      const detailedUser: UserDetailedInterface = state.detailedUsers[payloadUser.id];
      let updatedDetailedUser: UserDetailedInterface = {
        user: payloadUser,
        numAssessments: 0,
      };
      if(detailedUser) {
        updatedDetailedUser = {
          ...detailedUser,
          user: payloadUser,
        };
      }
      return {
        ...state,
        selectedProfileUser: payloadUser,
        users: { ...state.users, [payloadUser.id]:payloadUser },
        detailedUsers: { ...state.detailedUsers, [payloadUser.id]:updatedDetailedUser },
      };

    case UserActionType.LoadUserErgoTeamCompleted:
      return {
        ...state,
        selectedProfileErgoTeam: action.payload as ErgoTeamMemberInterface[],
      };

    case UserActionType.DeleteUserCompleted:
      const deletedUser: UserInterface = action.payload;
      const usersArray: UserInterface[] = Object.values(state.users)
        .filter((user: UserInterface) => {
          if(user.id !== deletedUser.id) {
            return user;
          }
        });
      const finalUsersKV: { [id: number]: UserInterface } = usersArray.reduce(
        (users: { [id: number]: UserInterface }, user: UserInterface) => {
          return { ...users, ...{
            [user.id]: user,
          }};
        },
        {});

      const detailedUserArray: UserDetailedInterface[] = Object.values(state.detailedUsers)
        .filter((detailedUser: UserDetailedInterface) => {
          if(detailedUser.user.id !== deletedUser.id) {
            return detailedUser;
          }
        });

      const finalDetailedUsersKV: { [id: number]: UserDetailedInterface } = detailedUserArray.reduce(
        (detailedUsers: { [id: number]: UserDetailedInterface }, detailedUser: UserDetailedInterface) => {
          return { ...detailedUsers, ...{
            [detailedUser.user.id]: detailedUser,
          }};
        },
        {});
      return {
        ...state,
        users: finalUsersKV,
        detailedUsers: finalDetailedUsersKV,
      };

    default:
      return state;
  }
}
